/**
 * CompleteProfileModal.js
 *
 * Shown when the backend rejects draft creation with a 403 + missing_fields.
 * Lets the user fill in the missing profile fields inline without leaving the wizard.
 *
 * Patchable fields: first_name, last_name, phone  (via PATCH /api/users/{id}/)
 * Read-only:        phone_verified                (requires OTP — shown as info only)
 *
 * On save: PATCHes the user, updates localStorage user_profile, then retries createDraft.
 */

import { useState } from 'react';
import { api } from '../../../api';

const FIELD_META = {
    first_name:     { label: 'نام',          type: 'text',  placeholder: 'نام خود را وارد کنید' },
    last_name:      { label: 'نام خانوادگی', type: 'text',  placeholder: 'نام خانوادگی خود را وارد کنید' },
    phone:          { label: 'شماره موبایل', type: 'tel',   placeholder: 'مثال: ۰۹۱۲۱۲۳۴۵۶۷' },
    phone_verified: { label: 'تأیید شماره موبایل', readOnly: true },
};

export default function CompleteProfileModal({ missingFields = [], onSuccess, onClose }) {
    // Separate editable fields from read-only ones
    const editableFields = missingFields.filter(f => !FIELD_META[f]?.readOnly);
    const readOnlyFields = missingFields.filter(f => FIELD_META[f]?.readOnly);

    const [values, setValues] = useState(
        Object.fromEntries(editableFields.map(f => [f, '']))
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (field, val) =>
        setValues(prev => ({ ...prev, [field]: val }));

    const handleSave = async () => {
        // Basic validation
        for (const f of editableFields) {
            if (!values[f]?.trim()) {
                setError(`لطفاً فیلد "${FIELD_META[f]?.label || f}" را پر کنید.`);
                return;
            }
        }
        setError('');
        setSaving(true);
        try {
            const profile = JSON.parse(localStorage.getItem('user_profile') || '{}');
            const userId = profile.id || localStorage.getItem('user_id');

            // PATCH the user profile
            const { data } = await api.patch(`/users/${userId}/`, values);

            // Update localStorage so the rest of the app reflects the change
            localStorage.setItem('user_profile', JSON.stringify({ ...profile, ...data }));

            onSuccess();
        } catch (err) {
            const detail = err?.response?.data;
            if (typeof detail === 'object') {
                const msgs = Object.entries(detail)
                    .map(([k, v]) => `${FIELD_META[k]?.label || k}: ${Array.isArray(v) ? v.join(', ') : v}`)
                    .join(' | ');
                setError(msgs);
            } else {
                setError('خطا در ذخیره اطلاعات. لطفاً دوباره تلاش کنید.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cpm-overlay" role="dialog" aria-modal="true" aria-label="تکمیل پروفایل">
            <div className="cpm-modal" dir="rtl">

                {/* ── Header ── */}
                <div className="cpm-header">
                    <span className="cpm-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                        </svg>
                    </span>
                    <div>
                        <h2 className="cpm-title">تکمیل پروفایل</h2>
                        <p className="cpm-subtitle">برای ثبت درخواست، اطلاعات زیر الزامی است.</p>
                    </div>
                    <button className="cpm-close" onClick={onClose} aria-label="بستن">✕</button>
                </div>

                {/* ── Editable fields ── */}
                <div className="cpm-body">
                    {editableFields.map(field => (
                        <div key={field} className="prw-field">
                            <label className="prw-label" htmlFor={`cpm-${field}`}>
                                {FIELD_META[field]?.label || field}
                            </label>
                            <input
                                id={`cpm-${field}`}
                                className="prw-input"
                                type={FIELD_META[field]?.type || 'text'}
                                placeholder={FIELD_META[field]?.placeholder || ''}
                                value={values[field]}
                                onChange={e => handleChange(field, e.target.value)}
                                autoComplete="off"
                            />
                        </div>
                    ))}

                    {/* ── Read-only notice (phone_verified etc.) ── */}
                    {readOnlyFields.length > 0 && (
                        <div className="cpm-notice">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            <span>
                                {readOnlyFields.includes('phone_verified')
                                    ? 'شماره موبایل شما هنوز تأیید نشده است. پس از ذخیره، از پروفایل خود اقدام به تأیید کنید.'
                                    : `فیلدهای ${readOnlyFields.join(', ')} نیاز به اقدام جداگانه دارند.`
                                }
                            </span>
                        </div>
                    )}

                    {error && <p className="prw-error">{error}</p>}
                </div>

                {/* ── Footer ── */}
                <div className="cpm-footer">
                    <button className="prw-btn prw-btn-secondary" onClick={onClose} disabled={saving}>
                        انصراف
                    </button>
                    <button
                        className="prw-btn prw-btn-primary"
                        onClick={handleSave}
                        disabled={saving || editableFields.length === 0}
                    >
                        {saving ? 'در حال ذخیره...' : 'ذخیره و ادامه'}
                    </button>
                </div>

            </div>
        </div>
    );
}
